import React, {useContext, useEffect, useRef, useState} from 'react';
import {PAContext} from "../../../Services/PAContext";
import MessageCard from "./MessageCard";
import {Chip, FormControlLabel, Switch, TextField, Box, Typography, Paper} from "@mui/material";
import './messages.css';
import {updateChipStyle} from "../../ButtonStyle/Update";
import usePA_GlobalStore from "../../../Services/ZustandGlobalStore";
import Message from "../../../MasterEventObjects/Message";



function MessagesList() {

    const { PA_GlobalObject, set_PA_GlobalObject } = usePA_GlobalStore();
    const { dataArray, sendSocketMessage, removeFirstMessagesActions } = useContext(PAContext);
    const { IncomingEventActions, Account, MessagesPipeline, authenticated } = dataArray;

    const messagesContainerRef = useRef(null);
    const [eventMessages, setEventMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [priority, setPriority] = useState(false);

    // 1. Keep local state synced with the Global Store (The Source of Truth)
    useEffect(() => {
        if (PA_GlobalObject?.Planning?.messages) {
            setEventMessages(PA_GlobalObject.Planning.messages);
        }
    }, [PA_GlobalObject.Planning.messages]);

    // 2. Handle Incoming Actions (e.g., Initial Load)
    useEffect(() => {
        for (let i = IncomingEventActions.length - 1; i >= 0; i--) {
            const _incoming = IncomingEventActions[i];

            switch (_incoming.action) {
                case "E_getEventMessagesResults":
                    // Update Global Store with the fetched list
                    // The Global Store handler handles duplicates, but we update locally for immediate UI response
                    setEventMessages(prevMessages => {
                        const existingIds = new Set(prevMessages.map(msg => msg.id || msg.temp_id));
                        const newMessages = _incoming["messages_list"].filter(msg => !existingIds.has(msg.id));
                        return [...prevMessages, ...newMessages];
                    });

                    IncomingEventActions.splice(i, 1);
                    break;
                default:
                    break;
            }
        }
    }, [IncomingEventActions]);

    // 3. Handle Message Pipeline (Success/Fail confirmations)
    useEffect(() => {
        if (MessagesPipeline.length === 0) return;

        MessagesPipeline.forEach(_incoming => {
            // Note: The actual state "swap" (removing isProcessing) happens in your
            // Global Store socket handler we wrote earlier.
            // This loop simply ensures the pipeline is cleared.
            removeFirstMessagesActions();
        });
    }, [MessagesPipeline, removeFirstMessagesActions]);

    // 4. Smooth Auto-Scroll
    useEffect(() => {
        if (messagesContainerRef.current) {
            setTimeout(() => {
                messagesContainerRef.current.scrollTo({
                    top: messagesContainerRef.current.scrollHeight,
                    behavior: "smooth",
                });
            }, 100);
        }
    }, [eventMessages]);

    // 5. Functional Send Message with Optimistic UI
    const handleSendMessage = () => {
        const textToSend = newMessage.trim();

        if (textToSend !== "" && PA_GlobalObject.Planning.status !== 10) {
            // 1. Instantiate class (generates temp_id internally)
            const messageObj = new Message();

            // 2. Populate fields
            messageObj.attendee_id = Account.id;
            messageObj.priority = priority;
            messageObj.message = textToSend;
            messageObj.created = Date.now(); // For immediate sorting

            // 3. UI Flag for "Sending..." state
            messageObj.isProcessing = true;

            // 4. Update Local State immediately (Optimistic Update)
            setEventMessages((prev) => [...prev, messageObj]);

            // 5. Send to Socket
            const _sendString = {
                action: "E_sendEventMessage",
                event_id: PA_GlobalObject.Planning.id,
                message_data: messageObj
            };
            sendSocketMessage(_sendString);

            // 6. Reset UI
            setNewMessage("");
            setPriority(false);
        }
    };


    return (

        <Box
            className="messages-list-container"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: 'calc(100vh - 202px)', // Important: ensure parent has a height
                maxHeight: '800px', // Or whatever your desired total height is
                bgcolor: '#f8fafc',
                borderRadius: '12px',
                overflow: 'hidden',
                border: '1px solid #e2e8f0'
            }}
        >

            {/* --- MESSAGES HEADER --- */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.6)',
                    boxShadow: '0 4px 24px -6px rgba(0,0,0,0.06)',
                    borderRadius: '12px 12px 0 0',
                    zIndex: 2
                }}
            >
                {/* --- MESSAGES HEADER --- */}
                <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                    {/* Left Side: Icon and Title */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)', // Blue gradient
                                p: 0.9,
                                borderRadius: '10px',
                                display: 'flex',
                                boxShadow: '0 4px 10px rgba(37, 99, 235, 0.3)'
                            }}>
                                <i className="icon-smartphone-text" style={{ color: 'white', fontSize: '18px' }} />
                            </Box>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b', fontSize: '1rem', letterSpacing: -0.4, whiteSpace: 'nowrap' }}>
                                    Messages
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                    <i className="icon-bubbles" style={{ color: '#2563eb', fontSize: '10px' }} />
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, fontSize: '0.65rem', letterSpacing: 0.1, whiteSpace: 'nowrap' }}>
                                        Event Discussion
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>

                    {/* Right Side: Live Chat Pill */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            bgcolor: '#f0fdf4',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '20px',
                            border: '1px solid #dcfce7'
                        }}
                    >
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: '#22c55e',
                                boxShadow: '0 0 8px #22c55e'
                            }}
                        />
                        <Typography
                            sx={{
                                fontSize: '11px',
                                fontWeight: 800,
                                color: '#166534',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em'
                            }}
                        >
                            Live
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            {/* 2. Message Area: Flex: 1 makes this take up all remaining space */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    bgcolor: '#ffffff'
                }}
                ref={messagesContainerRef}
            >
                {eventMessages?.length > 0 ? (
                    eventMessages
                        .sort((a, b) => a.created - b.created)
                        .map((_message, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    justifyContent: _message.attendee_id === Account.id ? 'flex-end' : 'flex-start',
                                    mb: 1.5
                                }}
                            >
                                <MessageCard
                                    message={_message}
                                    align={_message.attendee_id !== Account.id ? "left" : "right"}
                                />
                            </Box>
                        ))
                ) : (
                    <Box sx={{ m: 'auto', textAlign: 'center', opacity: 0.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>No Messages Yet</Typography>
                    </Box>
                )}
            </Box>

            {/* 3. Footer: Stays naturally at the bottom */}
            <Box
                sx={{
                    p: 2,
                    bgcolor: '#fff',
                    borderTop: '1px solid #e2e8f0',
                    borderRadius: '0 0 24px 24px' // Matching our app card style
                }}
            >
                <TextField
                    fullWidth
                    multiline           // Allows expansion
                    minRows={1}         // Starts at 1 line
                    maxRows={3}         // Stops expanding at 3 lines, then scrolls
                    variant="outlined"
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                        // Send on Enter, but allow New Line on Shift + Enter
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                        }
                    }}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '16px', // Slightly rounder for the "soft" look
                            bgcolor: '#f8fafc',
                            fontSize: '0.9rem',
                            transition: 'background-color 0.2s',
                            '&:hover': {
                                bgcolor: '#f1f5f9'
                            }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#e2e8f0'
                        }
                    }}
                />

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '12px'
                }}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={priority}
                                onChange={() => setPriority(!priority)}
                                size="small"
                                sx={{
                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                        color: '#f43f5e', // Rose color for priority
                                    },
                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                        backgroundColor: '#f43f5e',
                                    },
                                }}
                            />
                        }
                        label={
                            <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>
                                Priority
                            </Typography>
                        }
                    />

                    <Chip
                        sx={{
                            ...updateChipStyle,
                            px: 3,
                            height: '36px',
                            fontWeight: 800,
                            fontSize: '12px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            background: 'linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%)', // Our soft blue gradient
                            color: 'white',
                            boxShadow: '0 4px 12px rgba(14, 165, 233, 0.2)',
                            '&:hover': {
                                boxShadow: '0 6px 16px rgba(14, 165, 233, 0.3)',
                            }
                        }}
                        label="Send"
                        onClick={handleSendMessage}
                    />
                </Box>
            </Box>
        </Box>

    )
}

export default MessagesList;
